import React, { useEffect, useRef, useState } from "react";
import { NavLink, Col } from "reactstrap";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import LocationOnSharpIcon from "@mui/icons-material/LocationOnSharp";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import MailOutlineSharpIcon from "@mui/icons-material/MailOutlineSharp";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import TagIcon from "@mui/icons-material/Tag";
import PublicSharpIcon from "@mui/icons-material/PublicSharp";
import MoreHorizSharpIcon from "@mui/icons-material/MoreHorizSharp";
import ReplyAllOutlinedIcon from "@mui/icons-material/ReplyAllOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AddIcon from "@mui/icons-material/Add";
import axios from "../../API/axios";
import useNewsFeedStore from "../NewsFeed/useNewsFeedStore";
import { ListRowSkeleton } from "@skeleton";
import { post, people } from "../../config/imagesLink";
import { extractPostInfoFromGetPostResponse } from "../../utility/newsFeedPost";
import "./SearchResult.css";

const getTabFromRoute = (routeTab) => {
  const tab = String(routeTab || "").toLowerCase();

  switch (tab) {
    case "people":
      return 1;
    case "companies":
      return 2;
    case "jobs":
      return 3;
    case "posts":
      return 4;
    case "hashtags":
      return 5;
    case "articles":
      return 6;
    case "blogs":
      return 7;
    default:
      return 1;
  }
};

const SEARCH_TAB_ROUTES = {
  1: "People",
  2: "Companies",
  3: "Jobs",
  4: "Posts",
  5: "Hashtags",
  6: "Articles",
  7: "Blogs",
};

const parseTitleAndHandle = (title) => {
  const raw = String(title || "").trim();
  const match = raw.match(/^(.*?)\s*\((@[^)]+)\)\s*$/);
  if (match) {
    return { name: match[1].trim(), handle: match[2].trim() };
  }
  return { name: raw, handle: "" };
};

const truncateText = (value, max = 160) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}...`;
};

const JOB_DUMMY_META = [
  {
    company: "SkillersZone LLC",
    location: "Dubai (On-site)",
    salary: "$35K/yr - $45K/yr",
    posted: "2 weeks ago",
    jobType: "Full-time",
    workType: "On-site",
  },
  {
    company: "Sun Hikkan Group",
    location: "Dubai (Remote)",
    salary: "$28K/yr - $38K/yr",
    posted: "1 month ago",
    jobType: "Full-time",
    workType: "Remote",
  },
  {
    company: "BEST HR Consultancy",
    location: "Beirut (Hybrid)",
    salary: "$22K/yr - $32K/yr",
    posted: "4 days ago",
    jobType: "Contract",
    workType: "Hybrid",
  },
  {
    company: "Staffgate",
    location: "Andorra la Vella (On-site)",
    salary: "$30K/yr - $42K/yr",
    posted: "3 weeks ago",
    jobType: "Full-time",
    workType: "On-site",
  },
];

const PEOPLE_DUMMY_META = [
  {
    headline:
      "Front-end Developer | Ex-Capgemini | Angular | Typescript | React | Javascript | 4+ Years",
    location: "Dubai, United Arab Emirates",
    summary:
      "Summary: Frontend Developer with 4+ years of experience delivering scalable, high-performance web applications.",
    followers: "2K",
    mutual: "Gazala Qureshi is a mutual connection",
    degree: "2nd",
  },
  {
    headline: "Business Transformation Architect at MSH | Strategy | Delivery",
    location: "Mumbai, Maharashtra, India",
    summary:
      "Summary: Helping organizations modernize operations with practical transformation roadmaps and stakeholder alignment.",
    followers: "1.2K",
    mutual: "Akhtar Ali and 2 others are mutual connections",
    degree: "2nd",
  },
  {
    headline: "Facilities Planner at Shivam Electricals Pvt Ltd | Site engineer",
    location: "Dubai, UAE",
    summary:
      "Summary: Planning and coordinating facility operations with a focus on safety, uptime, and team collaboration.",
    followers: "860",
    mutual: "Housam Habash is a mutual connection",
    degree: "3rd",
  },
  {
    headline: "HR Specialist | Talent Acquisition | Employer Branding",
    location: "Riyadh, Saudi Arabia",
    summary:
      "Summary: Building hiring pipelines and candidate experience programs for growing mid-size teams.",
    followers: "540",
    mutual: "Md Sufiyan and 1 other are mutual connections",
    degree: "2nd",
  },
];

const COMPANY_DUMMY_META = [
  {
    headline: "Automotive · Manufacturing · 201-500 employees",
    location: "Dubai, United Arab Emirates",
    summary:
      "About: Mobility and automotive solutions company focused on quality manufacturing and regional distribution.",
    followers: "12K",
    mutual: "3 people in your network follow this company",
  },
  {
    headline: "Information Technology & Services · 501-1,000 employees",
    location: "Mumbai, Maharashtra, India",
    summary:
      "About: Digital engineering partner delivering product development, cloud, and enterprise transformation services.",
    followers: "28K",
    mutual: "5 people in your network follow this company",
  },
  {
    headline: "Human Resources · Staffing · 51-200 employees",
    location: "Beirut, Lebanon",
    summary:
      "About: Talent and staffing firm connecting skilled professionals with employers across the Middle East.",
    followers: "4.5K",
    mutual: "2 people in your network follow this company",
  },
  {
    headline: "Facilities & Engineering Services · 11-50 employees",
    location: "Riyadh, Saudi Arabia",
    summary:
      "About: Engineering and facilities services provider supporting commercial and industrial operations.",
    followers: "1.8K",
    mutual: "1 person in your network follows this company",
  },
];

const POST_DUMMY_META = [
  {
    author: "Ali shaikh Shaikh",
    title: "Facilities Planner at Shivam Electricals Pvt Ltd | Site engineer",
    content:
      "HIRING — Front-End Developer Opportunity | Dubai. Looking for React and TypeScript experience. Happy to connect and refer strong profiles.",
    date: "2w",
    degree: "2nd",
    likes: 31,
    comments: 8,
    reposts: 1,
  },
  {
    author: "Akhtar Ali",
    title: "Business Transformation Architect at MSH",
    content:
      "Hiring tip of the week: clear role expectations reduce interview drop-offs and improve candidate experience across mid-level roles.",
    date: "1w",
    degree: "3rd+",
    likes: 18,
    comments: 4,
    reposts: 2,
  },
  {
    author: "Housam Habash",
    title: "Product Designer | UX | Design Systems",
    content:
      "Sharing a short write-up on designing cleaner search result cards for professional networks. Feedback welcome from the community.",
    date: "3d",
    degree: "2nd",
    likes: 42,
    comments: 11,
    reposts: 3,
  },
];

const POST_DUMMY_ITEMS = POST_DUMMY_META.map((item, index) => ({
  id: `dummy-post-${index + 1}`,
  isDummy: true,
  type: "Post",
  title: item.content,
  additionalInfo: `${item.author}`,
  description: item.title,
  image: null,
  ...item,
}));

const ARTICLE_DUMMY_ITEMS = [
  {
    id: "dummy-article-1",
    isDummy: true,
    title: "How Remote Work Is Reshaping Career Growth in 2026",
    author: "Ali shaikh Shaikh",
    authorTitle: "Facilities Planner at Shivam Electricals Pvt Ltd",
    description:
      "A practical look at skills, visibility, and networking habits that help professionals grow while working remotely.",
    date: "10 July 2026 at 3:14 pm",
    image: null,
  },
  {
    id: "dummy-article-2",
    isDummy: true,
    title: "5 Hiring Mistakes Companies Still Make in Mid-Level Roles",
    author: "Akhtar Ali",
    authorTitle: "Business Transformation Architect at MSH",
    description:
      "From unclear job descriptions to slow feedback loops, these common gaps quietly reduce offer acceptance rates.",
    date: "8 July 2026 at 11:20 am",
    image: null,
  },
  {
    id: "dummy-article-3",
    isDummy: true,
    title: "Building a Strong Personal Brand on Professional Networks",
    author: "Housam Habash",
    authorTitle: "Product Designer",
    description:
      "Simple content habits and profile updates that help recruiters and peers understand your expertise faster.",
    date: "5 July 2026 at 6:45 pm",
    image: null,
  },
];

const BLOG_DUMMY_META = [
  {
    description:
      "Explore pressing global social issues like poverty, inequality, climate change, and education.",
    date: "Thursday, January 2, 2025",
  },
  {
    description:
      "Practical ways teams can improve hiring quality while keeping candidate experience strong.",
    date: "Monday, March 10, 2025",
  },
  {
    description:
      "A quick guide to workplace culture trends shaping careers across the Middle East.",
    date: "Friday, May 16, 2025",
  },
];

const pickDummy = (list, index) => list[index % list.length];

/** Append * so dummy/placeholder text is easy to spot vs live API data. */
const markDummy = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.endsWith("*") ? text : `${text} *`;
};

const looksLikeDateOnly = (value) => {
  const text = String(value || "").trim();
  if (!text) return false;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return true;
  const parsed = moment(text, [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"], true);
  return parsed.isValid() && text.length < 32;
};

const stripHtmlToText = (value) => {
  if (value == null) return "";
  const raw = String(value);
  if (typeof DOMParser !== "undefined") {
    return (
      new DOMParser().parseFromString(raw, "text/html").body.textContent || ""
    )
      .replace(/\s+/g, " ")
      .trim();
  }
  return raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

const SearchResult = ({ searchQuery = "" }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { tab: routeTab } = useParams();
  const { setIsNewsPageLoaded } = useNewsFeedStore();
  const searchQueryFromUrl = new URLSearchParams(location.search).get("q") || "";
  const searchQueryFromState = location.state?.searchQuery || "";
  const resolvedSearchQuery = searchQuery || searchQueryFromUrl || searchQueryFromState;

  const [activeTab, setActiveTab] = useState(() => getTabFromRoute(routeTab));
  const [isLoading, setIsLoading] = useState(true);
  const [nextBatchLoading, setNextBatchLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [inputValue, setInputValue] = useState(resolvedSearchQuery);
  const [debouncedInputValue, setDebouncedInputValue] = useState(
    resolvedSearchQuery.trim()
  );
  const [hasTabChanged, setHasTabChanged] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [options, setOptions] = useState([]);
  const [searchUser, setSearchUser] = useState([]);
  const [searchHashtag, setSearchHashtag] = useState([]);
  const [searchComp, setSearchComp] = useState([]);
  const [searchJob, setSearchJob] = useState([]);
  const [searchPost, setSearchPost] = useState([]);
  const [searchBlog, setSearchBlog] = useState([]);
  const [searchArticle, setSearchArticle] = useState([]);
  const [followedMap, setFollowedMap] = useState({});
  const isFetching = useRef(false);
  const debounceRef = useRef(null);
  const sessionId = localStorage.getItem("sessionId");
  const guestModeData = localStorage.getItem("guestMode");
  const language = localStorage.getItem("Lang");

  useEffect(() => {
    setIsNewsPageLoaded(true);
    return () => setIsNewsPageLoaded(false);
  }, [setIsNewsPageLoaded]);

  useEffect(() => {
    const nextSearchValue = resolvedSearchQuery.trim();

    if (nextSearchValue !== inputValue) {
      setInputValue(nextSearchValue);
    }
  }, [resolvedSearchQuery]);

  useEffect(() => {
    const trimmedValue = inputValue.trim();
    const params = new URLSearchParams(location.search);

    if (trimmedValue) {
      params.set("q", trimmedValue);
    } else {
      params.delete("q");
    }

    const nextSearch = params.toString();
    const normalizedNextSearch = nextSearch ? `?${nextSearch}` : "";

    if (normalizedNextSearch === location.search) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: normalizedNextSearch,
      },
      {
        replace: true,
        state: {
          ...(location.state || {}),
          searchQuery: trimmedValue,
        },
      }
    );
  }, [inputValue, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedInputValue(inputValue.trim());
    }, 1000);

    return () => clearTimeout(delayDebounce);
  }, [inputValue]);

  const clearResults = () => {
    setSearchUser([]);
    setSearchHashtag([]);
    setSearchComp([]);
    setSearchJob([]);
    setSearchPost([]);
    setSearchBlog([]);
    setSearchArticle([]);
    setOptions([]);
  };

  const enrichPostsFromApi = async (posts = []) => {
    if (!posts.length) return [];

    const token = localStorage.getItem("restrictedViewerToken") || "";
    const langKey = language === "en" ? "" : language || "";

    const enrichedList = await Promise.all(
      posts.map(async (item) => {
        if (!item?.id || item?.enriched) return item;

        try {
          const res = await axios.get(
            `/Posts/GetPostInfo?DeviceType=Web&PostId=${item.id}&RestrictedViewerToken=${token}&langKey=${langKey}`
          );
          const postInfo = extractPostInfoFromGetPostResponse(
            res?.data?.responseData
          );
          if (!postInfo) return item;

          const contentText = stripHtmlToText(
            postInfo.postContent ||
              postInfo.description ||
              postInfo.shortDescription ||
              ""
          );

          return {
            ...item,
            enriched: true,
            image: postInfo.userAvatar || item.image,
            userUid: postInfo.userUid || item.userUid,
            author: postInfo.userName || item.author,
            nameTag: postInfo.nameTag || item.nameTag,
            headline:
              postInfo.title ||
              postInfo.additionalInfo ||
              item.headline ||
              "",
            content: contentText,
            creationTime: postInfo.creationTime || item.creationTime,
            likeCount: postInfo.likeCount ?? item.likeCount,
            commentCount: postInfo.commentCount ?? item.commentCount,
            shareCount: postInfo.shareCount ?? item.shareCount,
            repostCount:
              postInfo.repostCount ??
              postInfo.shareCount ??
              item.repostCount,
            slug: postInfo.slug || item.slug,
          };
        } catch (error) {
          return item;
        }
      })
    );

    return enrichedList;
  };

  const addOptions = () => {
    if (isFetching.current) return;

    isFetching.current = true;
    setIsLoading(true);
    const searchValue = debouncedInputValue || "All";
    const data = {
      deviceType: "Web",
      pageNumber: page,
      pageSize: 10,
      langKey: language === "en" ? "" : language || "",
      search: searchValue,
      type:
        activeTab === 1
          ? "User"
          : activeTab === 2
            ? "Company"
            : activeTab === 3
              ? "Job"
              : activeTab === 4
                ? "Post"
                : activeTab === 5
                  ? "Hashtag"
                  : activeTab === 6
                    ? "Article"
                    : activeTab === 7
                      ? "Blog"
                      : "",
    };

    axios
      .post("SMFeatures/GlobalSearchWithFilter", data)
      .then(async (response) => {
        if (
          !response.data ||
          !response.data.responseData ||
          !response.data.responseData.searchdata
        ) {
          setIsError(true);
          setIsLoading(false);
          setNextBatchLoading(false);
          setHasMoreData(false);
          clearResults();
          return;
        }

        let data = response.data.responseData.searchdata;

        if (Array.isArray(data) && data.length === 0) {
          setIsError(true);
          setIsLoading(false);
          setNextBatchLoading(false);
          setHasMoreData(false);
          clearResults();
          return;
        }

        setIsError(false);
        data = data.map((item) => {
          const [type, followers] = String(item?.type || "").split(",");
          return {
            ...item,
            type: type?.trim(),
            followers: parseInt(followers, 10),
          };
        });

        const addUniqueItems = (prevList, nextList) => {
          if (page === 1) {
            return nextList;
          }

          const itemSet = new Set(prevList.map((item) => item.id));
          const uniqueNewItems = nextList.filter((item) => !itemSet.has(item.id));
          return [...prevList, ...uniqueNewItems];
        };

        const users = data.filter((item) => item.type === "User");
        const hashtags = data.filter((item) => item.type === "Hashtag");
        const companies = data.filter((item) => item.type === "Company");
        const jobs = data.filter((item) => item.type === "Job");
        let posts = data.filter((item) => item.type === "Post");
        const blogs = data.filter((item) => item.type === "Blog");
        const articles = data.filter((item) => item.type === "Article");

        setSearchUser((prev) => addUniqueItems(prev, users));
        setSearchHashtag((prev) => addUniqueItems(prev, hashtags));
        setSearchComp((prev) => addUniqueItems(prev, companies));
        setSearchJob((prev) => addUniqueItems(prev, jobs));
        setSearchPost((prev) => addUniqueItems(prev, posts));
        setSearchBlog((prev) => addUniqueItems(prev, blogs));
        setSearchArticle((prev) => addUniqueItems(prev, articles));

        setIsLoading(false);
        setNextBatchLoading(false);
        const nextTotalPages = response.data.responseData.paging?.totalPages || 1;
        setTotalPages(nextTotalPages);
        setHasMoreData(page < nextTotalPages);

        setOptions((prev) => {
          const postSet = new Set(prev.map((postItem) => postItem.id));
          const uniqueNewPosts = data.filter((postItem) => !postSet.has(postItem.id));
          return page === 1 ? uniqueNewPosts : [...prev, ...uniqueNewPosts];
        });

        if (posts.length > 0) {
          const enrichedPosts = await enrichPostsFromApi(posts);
          setSearchPost((prev) => {
            const byId = new Map(
              enrichedPosts.map((postItem) => [String(postItem.id), postItem])
            );
            return prev.map((postItem) => byId.get(String(postItem.id)) || postItem);
          });
        }
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
        setNextBatchLoading(false);
        setHasMoreData(false);
        clearResults();
      })
      .finally(() => {
        isFetching.current = false;
      });
  };

  useEffect(() => {
    clearResults();
    setIsError(false);
    setTotalPages(1);
    setHasMoreData(true);

    if (debouncedInputValue && debouncedInputValue.length < 3) {
      setIsLoading(false);
      setNextBatchLoading(false);
      setHasMoreData(false);
      return;
    }

    if (page !== 1) {
      setPage(1);
      return;
    }

    addOptions();
  }, [debouncedInputValue, activeTab]);

  useEffect(() => {
    if (page > 1) {
      setNextBatchLoading(true);
      addOptions();
    }
  }, [page]);

  useEffect(() => {
    const nextTab = getTabFromRoute(routeTab);

    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, routeTab]);

  const goToSearchTab = (tabIndex) => {
    setHasTabChanged(true);
    setPage(1);
    setActiveTab(tabIndex);
    const tabPath = SEARCH_TAB_ROUTES[tabIndex];
    if (tabPath) {
      navigate({
        pathname: `/SearchResult/${tabPath}`,
        search: location.search,
      });
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    const handleInfiniteScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 10 >=
          document.documentElement.scrollHeight &&
        !isLoading &&
        hasMoreData &&
        !isFetching.current &&
        page < totalPages
      ) {
        if (debounceRef.current) return;

        debounceRef.current = setTimeout(() => {
          setPage((prev) => prev + 1);
          debounceRef.current = null;
        }, 300);
      }
    };

    window.addEventListener("scroll", handleInfiniteScroll);

    return () => {
      window.removeEventListener("scroll", handleInfiniteScroll);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [hasMoreData, isLoading, page, totalPages]);

  const renderEmptyState = () => {
    if (isLoading && page === 1) {
      return (
        <ListRowSkeleton className="mt-3" count={5} rowHeight={80} showAvatar />
      );
    }

    if (isError) {
      return (
        <div className="mt-2 error-image-cont d-flex align-items-center justify-content-center">
          <img src={post.errorSmall} alt="404 Not Found" style={{ width: "250px" }} />
        </div>
      );
    }

    return null;
  };

  const normalizeSearchTitle = (value) => {
    if (value === null || value === undefined) return "";

    const rawValue = String(value);
    let parsedValue = null;

    try {
      parsedValue = JSON.parse(rawValue);
    } catch (error) {
      parsedValue = null;
    }

    if (Array.isArray(parsedValue)) {
      const tags = parsedValue
        .map((item) => item?.displayName)
        .filter(Boolean)
        .map((name) => `#${name}`);

      if (tags.length > 0) {
        return [...new Set(tags)].join(" ");
      }
    }

    let cleanedText = rawValue.replace(/\{\d*\}/g, " ");

    if (typeof DOMParser !== "undefined") {
      const parser = new DOMParser();
      cleanedText =
        parser.parseFromString(cleanedText, "text/html").body.textContent ||
        cleanedText;
    } else {
      cleanedText = cleanedText.replace(/<[^>]*>/g, " ");
    }

    cleanedText = cleanedText.replace(/\s+/g, " ").trim();

    if (!cleanedText) {
      const hashtags = [...rawValue.matchAll(/\/hashtag\/([^"'/?#\s>]+)/gi)].map(
        (match) => {
          try {
            return `#${decodeURIComponent(match[1])}`;
          } catch (error) {
            return `#${match[1]}`;
          }
        }
      );

      if (hashtags.length > 0) {
        return [...new Set(hashtags)].join(" ");
      }
    }

    return cleanedText;
  };

  const normalizeSearchMeta = (value) => {
    if (value === null || value === undefined) return "";

    const rawValue = String(value).trim();
    if (!rawValue) return "";

    let parsedValue = null;
    try {
      parsedValue = JSON.parse(rawValue);
    } catch (error) {
      parsedValue = null;
    }

    if (Array.isArray(parsedValue)) {
      const tags = parsedValue
        .map((item) => item?.displayName)
        .filter(Boolean)
        .map((name) => `#${name}`);

      return [...new Set(tags)].join(" ");
    }

    if (parsedValue && typeof parsedValue === "object") {
      return "";
    }

    let cleanedText = rawValue.replace(/\{\d*\}/g, " ");

    if (typeof DOMParser !== "undefined") {
      const parser = new DOMParser();
      cleanedText =
        parser.parseFromString(cleanedText, "text/html").body.textContent ||
        cleanedText;
    } else {
      cleanedText = cleanedText.replace(/<[^>]*>/g, " ");
    }

    return cleanedText.replace(/\s+/g, " ").trim();
  };

  const formatPostSearchItem = (item) => {
    const title = normalizeSearchTitle(item?.title);
    const additionalInfo = normalizeSearchMeta(item?.additionalInfo);
    const description = normalizeSearchMeta(item?.description);

    return {
      title,
      additionalInfo:
        additionalInfo && additionalInfo !== title ? additionalInfo : "",
      description:
        description && description !== title && description !== additionalInfo
          ? description
          : "",
    };
  };

  const getPostSearchLink = (item) => {
    const normalizedTitle = normalizeSearchTitle(item?.title || "");
    const isHashtagType = item?.type === "Hashtag";
    const looksLikeHashtag = normalizedTitle.startsWith("#") && !!item?.slug;

    if ((isHashtagType || looksLikeHashtag) && item?.slug) {
      return `/hashtag/${encodeURIComponent(item.slug)}`;
    }

    return `/Post/${item?.id}/${item?.slug || ""}`;
  };

  const formatBlogDate = (value) => {
    if (!value) return "";
    const parsed = moment(value);
    if (parsed.isValid()) {
      return parsed.format("dddd, MMMM D, YYYY");
    }
    return normalizeSearchMeta(value);
  };

  const splitJobMeta = (description) => {
    const text = normalizeSearchMeta(description);
    if (!text) return { location: "", salary: "", extra: "" };

    const parts = text
      .split(/\s*[·|•]\s*|\n+/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length === 1) {
      const looksLikeSalary = /\$|€|£|₹|\/yr|\/year|\/mo|salary|k\/yr/i.test(
        parts[0]
      );
      return looksLikeSalary
        ? { location: "", salary: parts[0], extra: "" }
        : { location: parts[0], salary: "", extra: "" };
    }

    return {
      location: parts[0] || "",
      salary: parts[1] || "",
      extra: parts.slice(2).join(" · "),
    };
  };

  const handleImageError = (event) => {
    event.target.onerror = null;
    event.target.src = people.DefaultProfile;
  };

  const handleFollowClick = async (event, type, entityId) => {
    event.preventDefault();
    event.stopPropagation();

    if (!entityId || guestModeData === "true") return;

    const key = `${type}-${entityId}`;
    const currentlyFollowed = Boolean(followedMap[key]);

    setFollowedMap((prev) => ({ ...prev, [key]: !currentlyFollowed }));

    try {
      if (type === "Company") {
        if (currentlyFollowed) {
          await axios.post("Entity/Unfollow/Company", {
            deviceType: "Web",
            sessionId,
            uiD_ID: entityId,
            langKey: language === "en" ? "" : language,
          });
        } else {
          await axios.post("Entity/Follow/Company", {
            deviceType: "Web",
            sessionId,
            uiD_ID: entityId,
            langKey: language === "en" ? "" : language,
          });
        }
      } else if (currentlyFollowed) {
        await axios.post("UserFollow/UnfollowUser", {
          deviceType: "Web",
          sessionId,
          userUID: entityId,
          langKey: language === "en" ? "" : language,
        });
      } else {
        await axios.post("UserFollow/FollowUser", {
          deviceType: "Web",
          sessionId,
          userUID: entityId,
          langKey: language === "en" ? "" : language,
        });
      }
    } catch (error) {
      setFollowedMap((prev) => ({ ...prev, [key]: currentlyFollowed }));
    }
  };

  const renderPeopleOrCompanyCard = (item, type, index) => {
    const displayTitle = normalizeSearchTitle(item?.title);
    const { name, handle } = parseTitleAndHandle(displayTitle);
    const dummy =
      type === "Company"
        ? pickDummy(COMPANY_DUMMY_META, index)
        : pickDummy(PEOPLE_DUMMY_META, index);

    const apiHeadline = normalizeSearchMeta(item?.additionalInfo);
    const apiLocation = normalizeSearchMeta(item?.description);
    const hasApiFollowers = item?.followers > 0;
    const apiSummary = normalizeSearchMeta(item?.summary || item?.shortDescription);

    const headlineIsDummy = !apiHeadline;
    const locationIsDummy = !apiLocation;
    const followersIsDummy = !hasApiFollowers;
    const summaryIsDummy = !apiSummary;
    const mutualIsDummy = true;

    const headline = apiHeadline || dummy.headline;
    const locationText = apiLocation || dummy.location;
    const followersLabel = hasApiFollowers
      ? `${item.followers}`
      : dummy.followers;
    const summary = apiSummary || dummy.summary;
    const mutual = dummy.mutual;
    const degree = type === "User" ? dummy.degree : "";
    const followKey = `${type}-${item?.id}`;
    const isFollowed =
      Boolean(followedMap[followKey]) || Boolean(item?.isFollowedByYou);
    const itemLink =
      type === "Company"
        ? `/Company/${item?.id}/${item?.slug || ""}`
        : `/User/${item?.id}`;
    const messageLink =
      type === "User" ? `/Message/${item?.id}` : null;

    return (
      <div
        key={`${type}-${item?.id || index}`}
        className="search-result-card sr-entity-card"
      >
        <div className="sr-people-card">
          <Link to={itemLink} className="sr-people-avatar-link">
            <img
              className="sr-people-avatar"
              src={item?.image || people.DefaultProfile}
              alt=""
              onError={handleImageError}
            />
          </Link>

          <div className="sr-people-body">
            <div className="sr-people-name-row">
              <Link to={itemLink} className="sr-people-name-link">
                <h1 className="sr-people-name">
                  {name || displayTitle}
                  {handle ? <span className="sr-handle">{handle}</span> : null}
                </h1>
              </Link>
              {type === "User" ? (
                <>
                  <VerifiedOutlinedIcon className="sr-verified-icon" />
                  {degree ? (
                    <span className="sr-degree">
                      · {markDummy(degree)}
                    </span>
                  ) : null}
                </>
              ) : null}
            </div>

            {headline ? (
              <p className="sr-people-headline">
                {headlineIsDummy ? markDummy(headline) : headline}
              </p>
            ) : null}

            {locationText ? (
              <p className="sr-people-location">
                <LocationOnSharpIcon className="ConnectionIcons" />
                <span>
                  {locationIsDummy ? markDummy(locationText) : locationText}
                </span>
              </p>
            ) : null}

            {summary ? (
              <p className="sr-people-summary">
                {summaryIsDummy
                  ? markDummy(
                      `${t("User Profile.Summary")}: ${truncateText(
                        summary.replace(/^Summary:\s*/i, "").replace(/^About:\s*/i, ""),
                        110
                      )}`
                    )
                  : truncateText(summary, 120)}
              </p>
            ) : null}

            <div className="sr-meta-row">
              {followersLabel ? (
                <span className="sr-meta-item">
                  <GroupsOutlinedIcon className="ConnectionIcons" />
                  <span>
                    {followersIsDummy
                      ? markDummy(
                          `${followersLabel} ${t("People.Followers")}`
                        )
                      : `${followersLabel} ${t("People.Followers")}`}
                  </span>
                </span>
              ) : null}
            </div>

            {mutual ? (
              <div className="sr-mutual-row">
                <img
                  className="sr-mutual-avatar"
                  src={people.DefaultProfile}
                  alt=""
                  onError={handleImageError}
                />
                <span className="sr-mutual-text">
                  {mutualIsDummy
                    ? markDummy(
                        type === "Company"
                          ? t("SearchJob.Network follows company")
                          : t("User Profile.Mutual Connections")
                      )
                    : mutual}
                </span>
              </div>
            ) : null}
          </div>

          <div className="sr-people-actions">
            <button
              type="button"
              className={`sr-action-btn ${isFollowed ? "sr-action-btn-filled" : ""}`}
              onClick={(e) => handleFollowClick(e, type, item?.id)}
            >
              {isFollowed ? (
                t("User Profile.Following")
              ) : (
                <>
                  <AddIcon className="sr-action-icon" />
                  {t("People.Follow")}
                </>
              )}
            </button>
            {type === "User" && messageLink ? (
              <Link
                to={messageLink}
                className="sr-action-btn sr-action-btn-ghost"
                onClick={(e) => e.stopPropagation()}
              >
                <MailOutlineSharpIcon className="sr-action-icon" />
                {t("Notifications.Message")}
              </Link>
            ) : (
              <Link
                to={itemLink}
                className="sr-action-btn sr-action-btn-ghost"
                onClick={(e) => e.stopPropagation()}
              >
                <PersonAddAlt1OutlinedIcon className="sr-action-icon" />
                {t("Manage Job.View")}
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderJobCard = (item, index) => {
    const dummy = pickDummy(JOB_DUMMY_META, index);
    const apiTitle = normalizeSearchTitle(item?.title);
    const designation = normalizeSearchMeta(item?.additionalInfo);
    const apiCompany = item?.compName || item?.companyName || "";
    const parsed = splitJobMeta(item?.description);
    const apiPosted =
      item?.creationTime && moment(item.creationTime).isValid()
        ? moment(item.creationTime).fromNow()
        : "";
    const apiJobType = item?.jbTypName || item?.jobType || "";
    const apiWorkType = item?.workLocationTypeName || item?.workType || "";

    const titleIsDummy = !apiTitle;
    const companyIsDummy = !apiCompany;
    const locationIsDummy = !parsed.location;
    const salaryIsDummy = !parsed.salary;
    const postedIsDummy = !apiPosted;
    const jobTypeIsDummy = !apiJobType;
    const workTypeIsDummy = !apiWorkType;

    const jobTitle = apiTitle || "Open Position";
    const companyName = apiCompany || dummy.company;
    const jobLocation = parsed.location || dummy.location;
    const salary = parsed.salary || dummy.salary;
    const posted = apiPosted || dummy.posted;
    const jobType = apiJobType || dummy.jobType;
    const workType = apiWorkType || dummy.workType;
    const itemLink = `/JobDetail/${item?.id}/${item?.slug || ""}`;

    return (
      <Link
        to={itemLink}
        key={`Job-${item?.id || index}`}
        className="search-result-card"
      >
        <div className="sr-job-card">
          <img
            className="sr-job-logo"
            src={item?.image || people.DefaultProfile}
            alt=""
            onError={handleImageError}
          />
          <div className="sr-job-body">
            <p className="sr-job-title">
              {titleIsDummy ? markDummy(jobTitle) : jobTitle}
            </p>
            <p className="sr-job-company">
              {companyIsDummy ? markDummy(companyName) : companyName}
            </p>
            {designation && designation !== companyName ? (
              <p className="sr-job-designation">{designation}</p>
            ) : null}
            <p className="sr-job-location">
              {locationIsDummy ? markDummy(jobLocation) : jobLocation}
            </p>
            <p className="sr-job-salary">
              {salaryIsDummy ? markDummy(salary) : salary}
            </p>
            <p className="sr-job-meta">
              {postedIsDummy ? markDummy(posted) : posted}
            </p>
            <div className="sr-job-badges">
              {jobType ? (
                <span className="sr-job-badge">
                  {jobTypeIsDummy ? markDummy(jobType) : jobType}
                </span>
              ) : null}
              {workType ? (
                <span className="sr-job-badge">
                  {workTypeIsDummy ? markDummy(workType) : workType}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const renderPostLikeCard = (item, type, index) => {
    if (type === "Article") {
      return renderArticleCard(item, index);
    }

    if (type === "Hashtag") {
      return renderHashtagCard(item, index);
    }

    return renderFeedPostCard(item, index);
  };

  const renderHashtagCard = (item, index) => {
    const formatted = formatPostSearchItem(item);
    const dummy = pickDummy(POST_DUMMY_META, index);
    const apiTitle = normalizeSearchTitle(item?.title);
    const authorIsDummy = !apiTitle;
    const authorName = apiTitle || "#trending";
    const apiContent = formatted.description;
    const contentIsDummy = !apiContent;
    const contentText =
      apiContent ||
      "Explore posts and conversations related to this hashtag.";
    const itemLink = `/HashTag/${encodeURIComponent(
      item?.slug || formatted.title || authorName
    )}`;

    return (
      <Link
        to={itemLink}
        key={`Hashtag-${item?.id || index}`}
        className="search-result-card"
      >
        <div className="sr-post-card">
          <div className="sr-post-header">
            <div
              className="sr-post-avatar d-flex align-items-center justify-content-center"
              style={{ background: "rgba(0,65,159,0.12)" }}
            >
              <TagIcon style={{ color: "var(--buttonBase, #00419f)" }} />
            </div>
            <div className="sr-post-author">
              <span className="sr-hashtag-chip">
                {authorIsDummy
                  ? markDummy(
                      authorName.startsWith("#")
                        ? authorName
                        : `#${authorName}`
                    )
                  : authorName.startsWith("#")
                    ? authorName
                    : `#${authorName}`}
              </span>
              <p className="sr-post-date">
                {contentIsDummy
                  ? markDummy("Popular hashtag conversations")
                  : "Hashtag"}
              </p>
            </div>
          </div>
          <p className="sr-post-content">
            {contentIsDummy
              ? markDummy(truncateText(contentText, 220))
              : truncateText(contentText, 220)}
          </p>
        </div>
      </Link>
    );
  };

  const renderFeedPostCard = (item, index) => {
    const isFullDummy = Boolean(item?.isDummy);
    const formatted = formatPostSearchItem(item);
    const dummy = pickDummy(POST_DUMMY_META, index);
    const itemLink = isFullDummy ? "#" : getPostSearchLink(item);

    const apiAuthor =
      item?.author ||
      parseTitleAndHandle(formatted.additionalInfo || "").name ||
      normalizeSearchMeta(item?.additionalInfo) ||
      "";

    const rawTitle = formatted.title || normalizeSearchTitle(item?.title);
    const rawDescription = formatted.description || normalizeSearchMeta(item?.description);
    const enrichedContent = stripHtmlToText(item?.content || "");

    // Global search often returns a date in title; prefer enriched post body
    let apiContent = enrichedContent;
    if (!apiContent) {
      if (rawTitle && !looksLikeDateOnly(rawTitle)) apiContent = rawTitle;
      else if (rawDescription && !looksLikeDateOnly(rawDescription)) {
        apiContent = rawDescription;
      }
    }

    const apiHeadline =
      item?.headline ||
      (rawDescription &&
      rawDescription !== apiContent &&
      !looksLikeDateOnly(rawDescription)
        ? rawDescription
        : "") ||
      "";

    const dateCandidate =
      item?.creationTime ||
      item?.createdOn ||
      item?.date ||
      (looksLikeDateOnly(rawTitle) ? rawTitle : "") ||
      (looksLikeDateOnly(rawDescription) ? rawDescription : "");
    const apiDate =
      dateCandidate && moment(dateCandidate).isValid()
        ? moment(dateCandidate).fromNow()
        : "";

    const authorIsDummy = isFullDummy || !apiAuthor;
    const headlineIsDummy = isFullDummy || !apiHeadline;
    const contentIsDummy = isFullDummy || !apiContent;
    const dateIsDummy = isFullDummy || !apiDate;

    const authorName = apiAuthor || dummy.author;
    const authorTitle = apiHeadline || dummy.title;
    const contentText = apiContent || dummy.content;
    const displayDate = apiDate || dummy.date;
    const degree = item?.connectionDegree || dummy.degree;

    const hasApiLikes = item?.likeCount != null || item?.likes != null;
    const hasApiComments = item?.commentCount != null || item?.comments != null;
    const hasApiReposts =
      item?.repostCount != null ||
      item?.shareCount != null ||
      item?.reposts != null;
    const likes = hasApiLikes
      ? item?.likeCount ?? item?.likes
      : dummy.likes;
    const comments = hasApiComments
      ? item?.commentCount ?? item?.comments
      : dummy.comments;
    const reposts = hasApiReposts
      ? item?.repostCount ?? item?.shareCount ?? item?.reposts
      : dummy.reposts;

    const followKey = `User-${item?.userUid || item?.id || index}`;
    const isFollowed = Boolean(followedMap[followKey]);
    const profileLink = item?.userUid
      ? `/User/${item.userUid}`
      : isFullDummy
        ? "#"
        : itemLink;

    const cardInner = (
      <div className={`sr-feed-post-card${isFullDummy ? " sr-dummy-card" : ""}`}>
        {isFullDummy ? (
          <div className="sr-dummy-badge">
            {t("SearchJob.Dummy data")} *
          </div>
        ) : null}

        <div className="sr-feed-post-top">
          <Link
            to={profileLink}
            className="sr-feed-post-avatar-link"
            onClick={(e) => {
              if (isFullDummy || !item?.userUid) e.preventDefault();
              e.stopPropagation();
            }}
          >
            <img
              className="sr-post-avatar"
              src={item?.image || people.DefaultProfile}
              alt=""
              onError={handleImageError}
            />
          </Link>

          <div className="sr-feed-post-meta">
            <div className="sr-feed-post-name-row">
              <span className="sr-post-author-name">
                {authorIsDummy ? markDummy(authorName) : authorName}
              </span>
              {item?.nameTag ? (
                <span className="sr-handle">{item.nameTag}</span>
              ) : null}
              <span className="sr-degree">
                ·{" "}
                {item?.connectionDegree
                  ? degree
                  : markDummy(degree)}
              </span>
            </div>
            <p className="sr-post-author-title">
              {headlineIsDummy
                ? markDummy(truncateText(authorTitle, 100))
                : truncateText(authorTitle, 100)}
            </p>
            <p className="sr-post-date sr-feed-post-time">
              {dateIsDummy ? markDummy(displayDate) : displayDate}
              <span className="sr-dot">·</span>
              <PublicSharpIcon className="sr-public-icon" />
            </p>
          </div>

          <div className="sr-feed-post-actions">
            <button
              type="button"
              className={`sr-action-btn sr-follow-inline ${
                isFollowed ? "sr-action-btn-filled" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (item?.userUid) {
                  handleFollowClick(e, "User", item.userUid);
                } else {
                  setFollowedMap((prev) => ({
                    ...prev,
                    [followKey]: !prev[followKey],
                  }));
                }
              }}
            >
              {isFollowed ? (
                t("User Profile.Following")
              ) : (
                <>
                  <AddIcon className="sr-action-icon" />
                  {t("People.Follow")}
                </>
              )}
            </button>
            <button
              type="button"
              className="sr-more-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              aria-label="More"
            >
              <MoreHorizSharpIcon />
            </button>
          </div>
        </div>

        <p className="sr-post-content sr-feed-post-body">
          {contentIsDummy
            ? markDummy(truncateText(contentText, 280))
            : truncateText(contentText, 280)}
        </p>

        <div className="sr-feed-post-footer">
          <span className="sr-post-footer-item">
            <ThumbUpOffAltIcon />
            <span>
              {hasApiLikes ? likes : markDummy(String(likes))}
            </span>
            <span className="sr-footer-label">{t("Newsfeed.Like")}</span>
          </span>
          <span className="sr-post-footer-item">
            <ChatBubbleOutlineIcon />
            <span>
              {hasApiComments ? comments : markDummy(String(comments))}
            </span>
            <span className="sr-footer-label">{t("Newsfeed.Comment")}</span>
          </span>
          <span className="sr-post-footer-item">
            <ReplyAllOutlinedIcon />
            <span>
              {hasApiReposts ? reposts : markDummy(String(reposts))}
            </span>
          </span>
          <span className="sr-post-footer-item">
            <SendOutlinedIcon />
            <span className="sr-footer-label">{t("Newsfeed.Share")}</span>
          </span>
        </div>
      </div>
    );

    if (isFullDummy) {
      return (
        <div
          key={`Post-dummy-${item?.id || index}`}
          className="search-result-card sr-dummy-link"
          role="article"
        >
          {cardInner}
        </div>
      );
    }

    return (
      <Link
        to={itemLink}
        key={`Post-${item?.id || index}`}
        className="search-result-card"
      >
        {cardInner}
      </Link>
    );
  };

  const renderArticleCard = (item, index) => {
    const isFullDummy = Boolean(item?.isDummy);
    const dummy = pickDummy(ARTICLE_DUMMY_ITEMS, index);
    const apiTitle = normalizeSearchTitle(item?.title);
    const apiAuthor = normalizeSearchMeta(item?.additionalInfo);
    const apiDescription = normalizeSearchMeta(item?.description);
    const dateCandidate = item?.creationTime || item?.createdOn || item?.date;
    const apiDate =
      dateCandidate && moment(dateCandidate).isValid()
        ? moment(dateCandidate).format("D MMMM YYYY [at] h:mm a")
        : "";

    const title = apiTitle || dummy.title;
    const author = apiAuthor || dummy.author;
    const authorTitle = item?.authorTitle || (isFullDummy || !apiAuthor ? dummy.authorTitle : "");
    const description = apiDescription || dummy.description;
    const displayDate = apiDate || dummy.date;

    const titleIsDummy = isFullDummy || !apiTitle;
    const authorIsDummy = isFullDummy || !apiAuthor;
    const descIsDummy = isFullDummy || !apiDescription;
    const dateIsDummy = isFullDummy || !apiDate;

    const cardContent = (
      <div className={`sr-article-card${isFullDummy ? " sr-dummy-card" : ""}`}>
        {isFullDummy ? (
          <div className="sr-dummy-badge">
            {t("SearchJob.Dummy data")} *
          </div>
        ) : null}
        <div className="sr-post-header">
          <img
            className="sr-post-avatar"
            src={item?.image || people.DefaultProfile}
            alt=""
            onError={handleImageError}
          />
          <div className="sr-post-author">
            <p className="sr-post-author-name">
              {authorIsDummy ? markDummy(author) : author}
            </p>
            {authorTitle ? (
              <p className="sr-post-author-title">
                {authorIsDummy ? markDummy(authorTitle) : authorTitle}
              </p>
            ) : null}
            <p className="sr-post-date">
              {dateIsDummy ? markDummy(displayDate) : displayDate}
            </p>
          </div>
        </div>
        <h2 className="sr-article-title">
          {titleIsDummy ? markDummy(title) : title}
        </h2>
        <p className="sr-post-content">
          {descIsDummy
            ? markDummy(truncateText(description, 220))
            : truncateText(description, 220)}
        </p>
        <div className="sr-post-footer">
          <span className="sr-post-footer-item">
            <ThumbUpOffAltIcon />
            {t("Newsfeed.Like")}
          </span>
          <span className="sr-post-footer-item">
            <ChatBubbleOutlineIcon />
            {t("Newsfeed.Comment")}
          </span>
          <span className="sr-post-footer-item">
            <ShareOutlinedIcon />
            {t("Newsfeed.Share")}
          </span>
          <span className="sr-post-footer-item">
            <BookmarkBorderOutlinedIcon />
            {t("Newsfeed.Save")}
          </span>
        </div>
      </div>
    );

    if (isFullDummy) {
      return (
        <div
          key={`Article-dummy-${item?.id || index}`}
          className="search-result-card sr-dummy-link"
          role="article"
        >
          {cardContent}
        </div>
      );
    }

    return (
      <Link
        to={`/Articles/${item?.id}/${item?.slug || ""}`}
        key={`Article-${item?.id || index}`}
        className="search-result-card"
      >
        {cardContent}
      </Link>
    );
  };

  const renderBlogCard = (item, index) => {
    const dummy = pickDummy(BLOG_DUMMY_META, index);
    const apiTitle = normalizeSearchTitle(item?.title);
    const description = normalizeSearchMeta(item?.description);
    const additionalInfo = normalizeSearchMeta(item?.additionalInfo);
    const dateLooksLikeDate = moment(item?.description).isValid();
    const blogSlug = item?.slug || item?.additionalInfo || item?.id || "";
    const itemLink = `/blogs/${blogSlug}`;

    const titleIsDummy = !apiTitle;
    const title = apiTitle || "Challenges Shaping Our World Today";

    const apiShortDesc = dateLooksLikeDate
      ? ""
      : description && description !== title
        ? description
        : additionalInfo &&
            additionalInfo !== title &&
            additionalInfo !== blogSlug
          ? additionalInfo
          : "";
    const descIsDummy = !apiShortDesc;
    const shortDesc = apiShortDesc || dummy.description;

    const dateIsDummy = !dateLooksLikeDate;
    const blogDate = dateLooksLikeDate
      ? formatBlogDate(item?.description)
      : dummy.date;

    return (
      <Link
        to={itemLink}
        key={`Blog-${item?.id || index}`}
        className="search-result-card sr-blog-card"
      >
        <img
          className="sr-blog-image"
          src={item?.image || people.DefaultProfile}
          alt=""
          onError={handleImageError}
        />
        <div className="sr-blog-content">
          <div className="sr-blog-title">
            {titleIsDummy ? markDummy(title) : title}
          </div>
          {shortDesc ? (
            <p className="sr-blog-desc">
              {descIsDummy
                ? markDummy(truncateText(shortDesc, 140))
                : truncateText(shortDesc, 140)}
            </p>
          ) : null}
          {blogDate ? (
            <p className="sr-blog-date">
              {dateIsDummy ? markDummy(blogDate) : blogDate}
            </p>
          ) : null}
        </div>
      </Link>
    );
  };

  const renderItems = (items, type) => {
    if (isLoading && page === 1) {
      return renderEmptyState();
    }

    // Show sample cards when API has no results yet (Article / Posts)
    let displayItems = items;
    if ((!items || items.length === 0) && type === "Article") {
      displayItems = ARTICLE_DUMMY_ITEMS;
    } else if ((!items || items.length === 0) && type === "Post") {
      displayItems = POST_DUMMY_ITEMS;
    }

    if (!displayItems.length) {
      return renderEmptyState();
    }

    if (type === "Blog") {
      return (
        <div className="sr-blog-grid">
          {displayItems.map((item, index) => renderBlogCard(item, index))}
        </div>
      );
    }

    return (
      <div className="search-result-list">
        {(type === "Article" || type === "Post") &&
        displayItems[0]?.isDummy ? (
          <p className="sr-dummy-note mb-2">
            {t("SearchJob.Sample results note")}
          </p>
        ) : null}
        {displayItems.map((item, index) => {
          if (type === "User" || type === "Company") {
            return renderPeopleOrCompanyCard(item, type, index);
          }
          if (type === "Job") {
            return renderJobCard(item, index);
          }
          if (type === "Article") {
            return renderArticleCard(item, index);
          }
          if (type === "Post" || type === "Hashtag") {
            return renderPostLikeCard(item, type, index);
          }
          return null;
        })}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return renderItems(searchUser, "User");
      case 2:
        return renderItems(searchComp, "Company");
      case 3:
        return renderItems(searchJob, "Job");
      case 4:
        return renderItems(searchPost, "Post");
      case 5:
        return renderItems(searchHashtag, "Hashtag");
      case 6:
        return renderItems(searchArticle, "Article");
      case 7:
        return renderItems(searchBlog, "Blog");
      default:
        return renderEmptyState();
    }
  };

  const getActiveItems = () => {
    switch (activeTab) {
      case 1:
        return searchUser;
      case 2:
        return searchComp;
      case 3:
        return searchJob;
      case 4:
        return searchPost;
      case 5:
        return searchHashtag;
      case 6:
        return searchArticle;
      case 7:
        return searchBlog;
      default:
        return [];
    }
  };

  return (
    <>
      <Helmet>
        <title>Search Results | Welinkjobs</title>
        <meta
          name="description"
          content="Browse search results across jobs, companies, posts, people, blogs, and hashtags."
        />
      </Helmet>

      <div className="search-result-page">
        <div className="headerSection d-block d-md-flex justify-content-between mt-4">
          <Col md={7}>
            <div className="friendsHeaderText p-2 pt-0 pb-0">
              <h1>{t("SearchJob.See all results")}</h1>
            </div>
          </Col>
          <Col md={5}>
            <div>
              <input
                type="text"
                className="form-control p-2"
                placeholder={t("SearchJob.See all results")}
                onChange={(e) => {
                  setHasTabChanged(false);
                  setPage(1);
                  setInputValue(e.target.value);
                }}
                value={inputValue}
              />
            </div>
          </Col>
        </div>

        <div className="job-history deskT d-flex flex-column ">
          <div className="tabButton d-flex p-2 w-100 me-2 connectionTab">
            <NavLink
              onClick={() => goToSearchTab(1)}
              className={`text-center ${activeTab === 1 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Sidemenubar.People")}</p>
            </NavLink>

            <NavLink
              onClick={() => goToSearchTab(2)}
              className={`text-center ${activeTab === 2 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Sidemenubar.Companies")}</p>
            </NavLink>

            <NavLink
              onClick={() => goToSearchTab(3)}
              className={`text-center ${activeTab === 3 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Company Profile.Jobs")}</p>
            </NavLink>

            <NavLink
              onClick={() => goToSearchTab(4)}
              className={`text-center ${activeTab === 4 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Company Profile.Posts")}</p>
            </NavLink>

            <NavLink
              onClick={() => goToSearchTab(5)}
              className={`text-center ${activeTab === 5 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Newsfeed.Hashtag")}</p>
            </NavLink>

            <NavLink
              onClick={() => goToSearchTab(6)}
              className={`text-center ${activeTab === 6 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Newsfeed.Article")}</p>
            </NavLink>
            <NavLink
              onClick={() => goToSearchTab(7)}
              className={`text-center ${activeTab === 7 ? "activeTab" : ""}`}
            >
              <p className="f-14 tabName">{t("Sidemenubar.Blogs")}</p>
            </NavLink>
          </div>

          <div className="tab-content p-2">
            {renderTabContent()}

            {nextBatchLoading && (
              <div className="text-center">
                <img src={post?.loader} width={50} alt="Loading" />
              </div>
            )}

            {!hasMoreData &&
              !isLoading &&
              getActiveItems().length > 0 &&
              page > 1 && (
                <p className="text-center mt-3">
                  {t("Newsfeed.No more data available")}
                </p>
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchResult;
